
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Line2} from "../../libs/three.js/lines/Line2.js";
import {LineGeometry} from "../../libs/three.js/lines/LineGeometry.js";
import {LineMaterial} from "../../libs/three.js/lines/LineMaterial.js";
import {createStyledLabel} from "./DigitizationStyle.js";
import {setLabelWanted, setLabelContent} from "./labels/labelState.js";
import {computeScreenSpaceScale, ScreenSpaceSizes} from "./ScreenSpaceScale.js";
import {createCopyIconMarker, wireCopyIconText, positionCopyIconAtLabelCorner} from "./CopyIconMarker.js";
import {computeHeightTriangle} from "./geometry/measurementGeometry.js";

import {t} from "./i18n/index.js";
// Matches the yellow already used elsewhere in this codebase for auxiliary/
// helper (not-directly-captured) geometry - pipeTool.js's own
// DIRECTION_COLOR/SEED_COLOR family, PipeVisual.js's AXIS_COLOR.
const AUX_COLOR = 0xffdc00;

function createDashedEdge(){
	const geometry = new LineGeometry();
	geometry.setPositions([0, 0, 0, 0, 0, 0]);
	const material = new LineMaterial({
		color: AUX_COLOR, linewidth: 2, dashSize: 0.3, gapSize: 0.18, dashed: true,
		resolution: new THREE.Vector2(1000, 1000),
	});
	material.depthTest = false;
	const line = new Line2(geometry, material);
	line.computeLineDistances();
	line.visible = false;
	return line;
}

function setLinePositions(line, a, b){
	line.position.copy(a);
	line.geometry.setPositions([0, 0, 0, ...b.clone().sub(a).toArray()]);
	line.geometry.verticesNeedUpdate = true;
	line.geometry.computeBoundingSphere();
	line.computeLineDistances();
}

// Same "mindst millimeter" precision as distanceTool.js's own formatLength().
function formatLength(meters){ return `${meters.toFixed(3)} m`; }
function formatAngle(degrees){ return `${degrees.toFixed(1)}°`; }

// Visual aid for Mål højde (see tools/heightTool.js): given the feature's
// own 2 real, registered points (A, B - placed/dragged via the normal
// FeatureVisual sphere mechanics), shows the auxiliary right triangle used
// to derive height/horizontal-distance/angle (see
// geometry/measurementGeometry.js's computeHeightTriangle - same
// construction Potree's own native height tool uses: the right angle sits
// at a derived corner point, which is NEVER drawn as an editable vertex -
// only the 2 real points are, matching the task's own "no vertex
// coordinates, but distance on the segments, and angle measurement"
// requirement).
//
// Deliberately NOT a registered Feature/FeatureVisual itself - purely a
// decorative child of the real LineString FeatureVisual (see
// heightTool.js: `visual.add(auxiliary)`), so it moves/hides/deletes
// together with that feature for free via ordinary THREE.Object3D parent/
// child semantics (visibility cascades, and scene.remove(visual) removes
// this whole subtree too) - no separate lifecycle wiring needed. The one
// thing parent/child alone does NOT give for free is the per-frame screen-
// space label/line update and the labelsVisible toggle, both driven
// through the small, generic `visual.userData.auxiliaryVisual` convention
// (see FeatureVisual.js's own setLabelsVisible()/DigitizationModule.js's
// update() loop) - a single duck-typed hook point, not Højde-specific
// code baked into the shared FeatureVisual class.
export class HeightAuxiliaryVisual extends THREE.Object3D {
	constructor(){
		super();

		this.horizontalEdge = createDashedEdge();
		this.verticalEdge = createDashedEdge();
		this.add(this.horizontalEdge);
		this.add(this.verticalEdge);

		this.horizontalLabel = createStyledLabel("");
		this.verticalLabel = createStyledLabel("");
		this.angleLabelLow = createStyledLabel("");
		this.angleLabelHigh = createStyledLabel("");
		for(const label of [this.horizontalLabel, this.verticalLabel, this.angleLabelLow, this.angleLabelHigh]){
			label.visible = false;
			this.add(label);
		}

		// No copy icon on the horizontal/vertical DISTANCE labels
		// (user-requested, matches FeatureVisual's own segment labels - see
		// its _rebuildSegmentLabels() comment) - only the angle labels keep
		// one, since those aren't "afstand" labels.
		this.angleLowCopyIcon = createCopyIconMarker();
		this.angleHighCopyIcon = createCopyIconMarker();
		wireCopyIconText(this.angleLowCopyIcon, () => this.angleLabelLow.text || null);
		wireCopyIconText(this.angleHighCopyIcon, () => this.angleLabelHigh.text || null);
		for(const icon of [this.angleLowCopyIcon, this.angleHighCopyIcon]){
			this.add(icon);
		}

		this.labelsVisible = true;
		this._triangle = null;
	}

	get triangle(){ return this._triangle; }

	setLabelsVisible(visible){
		this.labelsVisible = visible;
		this._refreshLabelVisibility();
	}

	_refreshLabelVisibility(){
		const has = !!this._triangle;
		for(const label of this.auxLabels()) setLabelWanted(label, has && this.labelsVisible);
	}
	// Labels, som LabelManager (labels/) styrer: hjælpelabels udfases tidligt og vises kun ved valg/nærzoom (LABEL-STRATEGI.md 12.4)
	auxLabels(){
		return [this.horizontalLabel, this.verticalLabel, this.angleLabelLow, this.angleLabelHigh];
	}

	// a, b: THREE.Vector3 world positions of the feature's own 2 real points
	// (either order - computeHeightTriangle resolves low/high by Z itself).
	updateFromPoints(a, b){
		if(!a || !b){
			this.horizontalEdge.visible = false;
			this.verticalEdge.visible = false;
			this._triangle = null;
			this._refreshLabelVisibility();
			return;
		}

		const tri = computeHeightTriangle([a.x, a.y, a.z], [b.x, b.y, b.z]); // ikke "t": det ville skygge for i18n-funktionen t()
		this._triangle = tri;

		const low = new THREE.Vector3(...tri.low);
		const high = new THREE.Vector3(...tri.high);
		const corner = new THREE.Vector3(...tri.corner);

		setLinePositions(this.horizontalEdge, low, corner);
		setLinePositions(this.verticalEdge, corner, high);
		this.horizontalEdge.visible = true;
		this.verticalEdge.visible = true;

		setLabelContent(this.horizontalLabel, t("Vandret: {0}", formatLength(tri.horizontalDistance)));
		this.horizontalLabel.position.copy(low).add(corner).multiplyScalar(0.5);

		setLabelContent(this.verticalLabel, t("Højde: {0}", formatLength(tri.verticalDistance)));
		this.verticalLabel.position.copy(corner).add(high).multiplyScalar(0.5);

		// offset a quarter of the way toward the corner rather than sitting
		// exactly on the vertex sphere, so the angle label doesn't visually
		// collide with the (still shown, still draggable) vertex marker
		setLabelContent(this.angleLabelLow, formatAngle(tri.angleAtLow));
		this.angleLabelLow.position.copy(low).lerp(corner, 0.25);

		setLabelContent(this.angleLabelHigh, formatAngle(tri.angleAtHigh));
		this.angleLabelHigh.position.copy(high).lerp(corner, 0.25);

		this._refreshLabelVisibility();
	}

	updateResolution(width, height){
		this.horizontalEdge.material.resolution.set(width, height);
		this.verticalEdge.material.resolution.set(width, height);
	}

	// Single per-frame entry point (matches PipeVisual.js's own
	// updateScale()-does-everything convention) - called via the generic
	// `userData.auxiliaryVisual.update(...)` hook, not a dedicated
	// DigitizationModule.js instanceof branch.
	update(camera, clientWidth, clientHeight){
		this.updateResolution(clientWidth, clientHeight);

		const labels = [this.horizontalLabel, this.verticalLabel, this.angleLabelLow, this.angleLabelHigh];
		for(const label of labels){
			if(!label.visible) continue;
			const scale = computeScreenSpaceScale(
				camera, label.getWorldPosition(new THREE.Vector3()), camera.position,
				clientWidth, clientHeight, ScreenSpaceSizes.label);
			label.scale.set(scale, scale, scale);
		}

		positionCopyIconAtLabelCorner(this.angleLowCopyIcon, this.angleLabelLow, camera, clientWidth, clientHeight);
		positionCopyIconAtLabelCorner(this.angleHighCopyIcon, this.angleLabelHigh, camera, clientWidth, clientHeight);
	}

	// GPU-resource teardown, called generically via the `userData.auxiliaryVisual`
	// hook from FeatureVisual.dispose() when the owning Mål højde feature is
	// deleted - every object created in the constructor above is created
	// exactly once and kept for this visual's whole lifetime, so this is the
	// only place any of it needs releasing.
	dispose(){
		for(const edge of [this.horizontalEdge, this.verticalEdge]){
			edge.geometry.dispose();
			edge.material.dispose();
		}
		for(const label of [this.horizontalLabel, this.verticalLabel, this.angleLabelLow, this.angleLabelHigh]){
			label.dispose();
		}
		for(const icon of [this.angleLowCopyIcon, this.angleHighCopyIcon]){
			icon.geometry.dispose();
			icon.material.map.dispose();
			icon.material.dispose();
		}
	}
}
